import { useState } from 'react';

import { Close, ErrorOutline, InfoOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { Box, SxProps, Theme } from '@mui/material';

const customStyles = {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderWidth: '1px 1px 1px 5px',
    padding: '8px 16px 8px 14px',
    borderStyle: 'solid',
    borderRadius: '12px',
    fontSize: '14px',
    lineHeight: '22px',
  },
  info: {
    backgroundColor: '#D8EFFE',
    borderColor: (theme) => theme.palette.primary.main,
    color: '#3B3C40',
    '& svg': {
      color: (theme) => theme.palette.primary.main,
    },
  },
  error: {
    backgroundColor: '#FFE4D8',
    borderColor: (theme) => theme.palette.error.main,
    color: '#DB2E46',
    '& svg': {
      color: (theme) => theme.palette.error.main,
    },
  },
  warning: {
    backgroundColor: '#FFF3CD',
    borderColor: (theme) => theme.palette.warning.main,
    color: '#856404',
    '& svg': {
      color: (theme) => theme.palette.warning.main,
    },
  },
  closeIcon: {
    cursor: 'pointer',
    marginLeft: 'auto',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    height: '22px',
    flexShrink: 0,
  },
} satisfies Record<string, SxProps<Theme>>;

interface InformationBannerProps {
  message: string | JSX.Element;
  type?: 'info' | 'error' | 'warning';
  allowClose?: boolean;
  alignItems?: 'center' | 'flex-start';
}

export function InformationBanner({
  message,
  type = 'info',
  allowClose = false,
  alignItems = 'center',
}: InformationBannerProps) {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  return (
    <Box
      sx={
        {
          ...customStyles.base,
          ...customStyles[type],
          alignItems,
        } as SxProps<Theme>
      }
    >
      <Box sx={alignItems === 'flex-start' ? customStyles.iconContainer : undefined}>
        {type === 'info' && (
          <InfoOutlined
            fontSize='inherit'
            color='inherit'
          />
        )}
        {type === 'error' && (
          <ErrorOutline
            fontSize='inherit'
            color='inherit'
          />
        )}
        {type === 'warning' && (
          <WarningAmberOutlined
            fontSize='inherit'
            color='inherit'
          />
        )}
      </Box>
      {message}
      {allowClose && (
        <Close
          fontSize='small'
          color='inherit'
          sx={customStyles.closeIcon}
          onClick={() => setIsVisible(false)}
        />
      )}
    </Box>
  );
}
